'use client'
import {dollarsPrettifier} from '~/utils/dollarsPrettifier'
import {type PricingCardHeadingProps} from './types'
import styles from './styles.module.css'
import {
  type CSSProperties,
  type Ref,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

const CHARACTER_STAGGER_MS = 66

function Price({
  annualPrice,
  monthlyPrice,
}: {
  annualPrice: string
  monthlyPrice: string
}) {
  // Ensure both prices are the same length and fill with spaces if necessary
  // This ensures that we're always animating the same number of characters
  const maxLength = Math.max(annualPrice.length, monthlyPrice.length)
  const paddedAnnualPrice = annualPrice.padEnd(maxLength, ' ')
  const paddedMonthlyPrice = monthlyPrice.padEnd(maxLength, ' ')

  return (
    <div className={styles.priceDigitOuterContainer} aria-hidden="true">
      {paddedAnnualPrice.split('').map((char, index) => (
        <div
          key={`${char}-${index}`}
          style={
            {
              '--initial-delay': `${index * CHARACTER_STAGGER_MS}ms`,
              '--max-delay': `${(paddedAnnualPrice.length - 1) * CHARACTER_STAGGER_MS}ms`,
            } as CSSProperties
          }
          className={styles.priceDigitInnerContainer}
        >
          <span className={styles.priceDigit}>{char}</span>
          <span className={styles.priceDigit}>{paddedMonthlyPrice[index]}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * We need to render the price invisibly to measure its width for the container
 * We split the price into individual characters to ensure accurate width measurement
 * The dollar sign is handled separately since we don't want to animate it. */
function InvisiblePriceForWidthCalculation({
  price,
  ref,
}: {
  price: string
  ref: Ref<HTMLSpanElement>
}) {
  return (
    <span className="invisible absolute flex" ref={ref}>
      <span>$</span>
      {price.split('').map((char, index) => (
        <span key={index}>{char}</span>
      ))}
    </span>
  )
}

export const HgPricingCardHeading = ({
  standardPlanNameText,
  accentPlanNameText,
  pricePerMonth,
  annualPricePerMonth,
  planDescription,
  isMonthly,
}: PricingCardHeadingProps) => {
  const priceRef = useRef<HTMLSpanElement>(null)
  const [priceWidth, setPriceWidth] = useState(0)
  const [isResizingWindow, setIsResizingWindow] = useState(false)

  useEffect(() => {
    if (!priceRef.current) return

    const resizeObserver = new ResizeObserver(([entry]) => {
      setPriceWidth(entry.contentRect.width)
    })

    resizeObserver.observe(priceRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // Do not show width animation while resizing the window
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout
    const handleResize = () => {
      setIsResizingWindow(true)
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        setIsResizingWindow(false)
      }, 150)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimeout)
    }
  }, [])

  const formattedPricePerMonth = useMemo(
    () => dollarsPrettifier(pricePerMonth, false, 'twoWhenNecessary'),
    [pricePerMonth]
  )

  const formattedAnnualPricePerMonth = useMemo(
    () => dollarsPrettifier(annualPricePerMonth, false, 'twoWhenNecessary'),
    [annualPricePerMonth]
  )

  const displayedPrice = isMonthly
    ? formattedPricePerMonth
    : formattedAnnualPricePerMonth

  return (
    <div className="flex flex-col">
      <header className="flex flex-row items-center justify-start space-x-s2">
        <h1 className="text-text-default arcadia-heading-5">
          <span>{standardPlanNameText}</span>{' '}
          <span className="text-text-primary empty:hidden">
            {accentPlanNameText}
          </span>
        </h1>
      </header>
      <div
        className="flex text-text-subdued arcadia-subheading-5"
        aria-hidden="true"
      >
        <div
          style={
            {
              '--width': !priceWidth ? 'auto' : `${priceWidth}px`,
            } as CSSProperties
          }
          data-pricing={isMonthly ? 'monthly' : 'annual'}
          data-should-animate={pricePerMonth !== annualPricePerMonth}
          data-resizing={isResizingWindow}
          className={styles.priceContainer}
        >
          <span>$</span>
          <Price
            annualPrice={formattedAnnualPricePerMonth}
            monthlyPrice={formattedPricePerMonth}
          />
        </div>
        <InvisiblePriceForWidthCalculation price={displayedPrice} ref={priceRef} />
        <span>/mo.</span>
      </div>
      <p aria-live="polite" className="sr-only">{`$${displayedPrice} per month`}</p>
      <p className="pt-s2 text-text-subdued arcadia-body-3">{planDescription}</p>
    </div>
  )
}
